import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { config } from '@/config';
import type { ListNotificationsResponse, Notification } from '@/api/notify';

// useNotificationsStream opens a long-lived Server-Sent Events connection
// to /api/v1/organizations/{slug}/notifications/stream and pushes any new
// notifications into the React-Query cache for ['notifications', slug].
// The bell renders from that cache, so the dropdown updates the instant
// the server fires `notification_created` — no polling round-trip in the
// happy path.
//
// Reconnect strategy: fetch-event-source auto-retries on network errors
// with the delay we return from `onerror`. On every (re)open we call
// `invalidateQueries` so the bell catches up on anything that fired
// during the gap (covers the case where the server kept producing rows
// while the client was offline).
//
// Failure mode: if the stream can never connect (proxy strips the
// connection, browser blocks fetch streaming, etc.), the existing
// polling in NotificationBell stays as the safety net — the bell still
// updates, just with the polling cadence.

const STREAM_RETRY_MIN_MS = 1_000;
const STREAM_RETRY_MAX_MS = 30_000;

class FatalStreamError extends Error {}

interface NotificationCreatedEvent {
  /** Server includes every field the bell needs; same shape as list items. */
  id: string;
  kind: string;
  severity: Notification['severity'];
  title: string;
  body?: string;
  action_url?: string;
  metadata?: unknown;
  audit_log_id?: string;
  read_at?: string;
  created_at: string;
}

/** Payload of a server-fired `job_progress` event. Mirrors notify.JobProgress in Go. */
export interface JobProgressEvent {
  job_id: string;
  kind: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  progress: { done: number; total: number };
  metadata?: unknown;
  updated_at: string;
}

// In-memory dispatch registry for `job_progress` events. Keyed by job_id;
// a job can have multiple listeners (e.g. a dialog AND a list-row both
// watching the same job), so we hold a Set per key. The registry lives at
// module scope because the SSE connection is one-per-app — listeners
// subscribe directly to the registry without re-opening the stream.
const jobListeners = new Map<string, Set<(e: JobProgressEvent) => void>>();

/**
 * Subscribe to live progress events for a specific job. Returns an
 * unsubscribe function — the caller MUST call it on unmount or when the
 * job is no longer of interest, otherwise the listener leaks for the
 * life of the page.
 *
 * The subscription rides the existing notifications SSE connection, so
 * it costs nothing on the wire beyond the per-event JSON parse.
 */
export function subscribeToJob(
  jobID: string,
  cb: (e: JobProgressEvent) => void,
): () => void {
  let set = jobListeners.get(jobID);
  if (!set) {
    set = new Set();
    jobListeners.set(jobID, set);
  }
  set.add(cb);
  return () => {
    const s = jobListeners.get(jobID);
    if (!s) return;
    s.delete(cb);
    if (s.size === 0) jobListeners.delete(jobID);
  };
}

export function useNotificationsStream(
  slug: string | undefined,
  token: string | null,
) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!slug || !token) return;
    const ctrl = new AbortController();
    let retryDelayMs = STREAM_RETRY_MIN_MS;

    const url = `${config.apiURL}/api/v1/organizations/${encodeURIComponent(slug)}/notifications/stream`;
    const queryKey = ['notifications', slug] as const;

    void fetchEventSource(url, {
      signal: ctrl.signal,
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'text/event-stream',
      },
      // openWhenHidden keeps the stream alive when the tab backgrounds,
      // matching SSE's design. fetch-event-source defaults to closing on
      // hidden which would force a reconnect every time the user switches
      // tabs — annoying and unnecessary.
      openWhenHidden: true,

      async onopen(response) {
        if (response.ok && response.headers.get('content-type')?.startsWith('text/event-stream')) {
          // Reset backoff after a successful (re)open.
          retryDelayMs = STREAM_RETRY_MIN_MS;
          // Reconcile: anything the server fired during the gap is in the
          // list endpoint by now. Invalidate so the bell re-fetches.
          void queryClient.invalidateQueries({ queryKey });
          return;
        }
        // 401 → token expired; bail and let the auth flow refresh.
        // 4xx other → likely a permanent config issue; bail.
        if (response.status >= 400 && response.status < 500 && response.status !== 429) {
          throw new FatalStreamError(`stream open failed: ${response.status}`);
        }
        // 5xx / network: fall into onerror → retry.
        throw new Error(`stream open failed: ${response.status}`);
      },

      onmessage(ev) {
        if (ev.event === 'notification_created') {
          let payload: NotificationCreatedEvent;
          try {
            payload = JSON.parse(ev.data) as NotificationCreatedEvent;
          } catch {
            // Malformed event — ignore. Reconnect-and-refetch will catch up.
            return;
          }
          queryClient.setQueryData<ListNotificationsResponse>(queryKey, (prev) => {
            if (!prev) return prev;
            // Idempotency: if the row is already in the list (e.g. a poll
            // landed first), skip rather than dupe.
            if (prev.items.some((n) => n.id === payload.id)) return prev;
            const incoming: Notification = payload;
            const total = prev.total + 1;
            const unread = prev.unread + (payload.read_at ? 0 : 1);
            return {
              ...prev,
              items: [incoming, ...prev.items],
              total,
              unread,
            };
          });
          return;
        }
        if (ev.event === 'job_progress') {
          let payload: JobProgressEvent;
          try {
            payload = JSON.parse(ev.data) as JobProgressEvent;
          } catch {
            return;
          }
          const set = jobListeners.get(payload.job_id);
          if (!set) return;
          // Snapshot first — a listener may unsubscribe itself during the
          // callback (typical for terminal-status dialogs).
          for (const cb of Array.from(set)) {
            try { cb(payload); } catch { /* listener error is its own problem */ }
          }
        }
      },

      onerror(err) {
        if (err instanceof FatalStreamError) {
          // Don't retry — surface the error.
          throw err;
        }
        // Exponential backoff up to the cap. fetch-event-source uses the
        // returned number as the next delay before retrying.
        const delay = retryDelayMs;
        retryDelayMs = Math.min(retryDelayMs * 2, STREAM_RETRY_MAX_MS);
        return delay;
      },

      onclose() {
        // Server closed the stream cleanly. Throw to trigger a reconnect
        // via onerror; left alone, fetch-event-source treats onclose as
        // "we're done" and stops.
        throw new Error('stream closed by server');
      },
    }).catch((err) => {
      // Aborts are expected on unmount; everything else is logged.
      if (ctrl.signal.aborted) return;
      console.warn('notifications stream gave up:', err);
    });

    return () => {
      ctrl.abort();
    };
  }, [slug, token, queryClient]);
}
